<template>
  <Layout>
    <div class="privacy-page">
      <!-- Hero Section -->
      <section class="privacy-hero bg-gradient-primary text-white py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-8 mx-auto text-center">
              <h1 class="display-4 font-weight-bold mb-3">Privacy Policy</h1>
              <p class="lead mb-4">Effective Date: August 7, 2025 | Version: 2.1</p>
              <div class="d-flex justify-content-center">
                <b-button variant="light" class="mr-3" @click="scrollToDetails">
                  <b-icon icon="arrow-down" class="mr-2"></b-icon>
                  View Policy
                </b-button>
                <b-button variant="outline-light" @click="downloadPolicy">
                  <b-icon icon="download" class="mr-2"></b-icon>
                  Download PDF
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <div class="container my-5">
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <!-- Introduction -->
            <b-card no-body class="border-0 shadow-sm mb-4">
              <b-card-body>
                <div class="d-flex align-items-center mb-4">
                  <div class="icon-circle bg-primary text-white mr-3">
                    <i class="mdi mdi-shield-lock-outline" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="mb-0">Your Privacy Matters</h3>
                </div>
                <p>At OVVURA PORTAL L.L.C, your privacy and security are our top priority. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our Platform to book beauty, healthcare, and wellness services, shop our curated products, or engage with other OVVURA services. By accessing or using the Platform, you agree to this Privacy Policy.</p>
              </b-card-body>
            </b-card>

            <!-- Information We Collect -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="information-collected">
              <b-card-header class="bg-light">
                <h4 class="mb-0">1. Information We Collect</h4>
              </b-card-header>
              <b-card-body>
                <p>We collect personal data to provide and improve our services. This may include:</p>
                
                <div class="data-categories-grid">
                  <div class="data-category">
                    <div class="category-icon bg-info text-white">
                      <i class="mdi mdi-account"></i>
                    </div>
                    <h5>Identification & Contact</h5>
                    <p>Name, email address, phone number, postal address</p>
                  </div>
                  
                  <div class="data-category">
                    <div class="category-icon bg-success text-white">
                      <i class="mdi mdi-credit-card"></i>
                    </div>
                    <h5>Payment Details</h5>
                    <p>Card number, expiry date, billing address (used for processing, never stored)</p>
                  </div>
                  
                  <div class="data-category">
                    <div class="category-icon bg-warning text-white">
                      <i class="mdi mdi-heart-pulse"></i>
                    </div>
                    <h5>Health & Wellness</h5>
                    <p>Medical history or preferences when relevant to a booked service</p>
                  </div>
                  
                  <div class="data-category">
                    <div class="category-icon bg-primary text-white">
                      <i class="mdi mdi-history"></i>
                    </div>
                    <h5>Transactional</h5>
                    <p>Order history, booking details, transaction timestamps</p>
                  </div>
                  
                  <div class="data-category">
                    <div class="category-icon bg-secondary text-white">
                      <i class="mdi mdi-devices"></i>
                    </div>
                    <h5>Device & Usage</h5>
                    <p>IP address, browser type, operating system, cookies</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- How We Use Information -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="information-use">
              <b-card-header class="bg-light">
                <h4 class="mb-0">2. How We Use Your Information</h4>
              </b-card-header>
              <b-card-body>
                <div class="usage-features">
                  <div class="usage-feature">
                    <i class="mdi mdi-calendar-check text-primary"></i>
                    <span>Process bookings, transactions, and payments</span>
                  </div>
                  <div class="usage-feature">
                    <i class="mdi mdi-email-send text-primary"></i>
                    <span>Send confirmations, reminders, and (with consent) promotional communications</span>
                  </div>
                  <div class="usage-feature">
                    <i class="mdi mdi-face-agent text-primary"></i>
                    <span>Provide customer support and respond to inquiries</span>
                  </div>
                  <div class="usage-feature">
                    <i class="mdi mdi-shield-account text-primary"></i>
                    <span>Detect and prevent fraud or unlawful activity</span>
                  </div>
                  <div class="usage-feature">
                    <i class="mdi mdi-chart-bar text-primary"></i>
                    <span>Analyze trends and user preferences to enhance our services</span>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Data Retention -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="data-retention">
              <b-card-header class="bg-light">
                <h4 class="mb-0">3. Data Retention</h4>
              </b-card-header>
              <b-card-body>
                <div class="d-flex align-items-start">
                  <!-- <img src="@/assets/images/data-retention.png" alt="Data Retention" class="img-fluid rounded mr-4" style="max-width: 200px;"> -->
                  <div>
                    <p>We retain personal data only as long as necessary to fulfill the purposes for which it was collected or as required by law. You may request deletion of your account and personal data; we will comply subject to legal obligations.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Data Sharing -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="data-sharing">
              <b-card-header class="bg-light">
                <h4 class="mb-0">4. Data Sharing & Disclosure</h4>
              </b-card-header>
              <b-card-body>
                <p>We do not sell, rent, or lease your personal data. We may share information with:</p>
                
                <div class="sharing-partners">
                  <div class="partner-card">
                    <div class="partner-icon bg-primary text-white">
                      <i class="mdi mdi-account-group"></i>
                    </div>
                    <h5>Service Providers</h5>
                    <p>Third parties who help operate the Platform, process payments, manage bookings, deliver products, or provide support</p>
                  </div>
                  
                  <div class="partner-card">
                    <div class="partner-icon bg-warning text-white">
                      <i class="mdi mdi-scale-balance"></i>
                    </div>
                    <h5>Legal Authorities</h5>
                    <p>When required by law, regulation, or legal process</p>
                  </div>
                  
                  <div class="partner-card">
                    <div class="partner-icon bg-info text-white">
                      <i class="mdi mdi-bank-transfer"></i>
                    </div>
                    <h5>Business Transfers</h5>
                    <p>In the event of a merger, acquisition, or sale of assets. You will be notified if your data is transferred</p>
                  </div>
                </div>
                
                <div class="security-note p-4 mt-4">
                  <i class="mdi mdi-shield-check text-primary mr-2"></i>
                  <strong>Important:</strong> All credit/debit card details and personally identifiable information will NOT be stored, sold, shared, rented, or leased to any third parties. OVVURA will not pass your credit/debit card details on to third parties at any time.
                </div>
              </b-card-body>
            </b-card>

            <!-- Security -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="security">
              <b-card-header class="bg-light">
                <h4 class="mb-0">5. Security of Your Information</h4>
              </b-card-header>
              <b-card-body>
                <div class="security-features">
                  <div class="security-feature">
                    <div class="security-icon bg-success text-white">
                      <i class="mdi mdi-lock"></i>
                    </div>
                    <h5>Encryption</h5>
                    <p>Industry-standard encryption for all sensitive data</p>
                  </div>
                  
                  <div class="security-feature">
                    <div class="security-icon bg-primary text-white">
                      <i class="mdi mdi-server"></i>
                    </div>
                    <h5>Secure Servers</h5>
                    <p>Protected infrastructure with regular security audits</p>
                  </div>
                  
                  <div class="security-feature">
                    <div class="security-icon bg-info text-white">
                      <i class="mdi mdi-shield-key"></i>
                    </div>
                    <h5>Access Controls</h5>
                    <p>Strict access limitations to personal data</p>
                  </div>
                </div>
                
                <div class="alert alert-light mt-4">
                  <i class="mdi mdi-alert-circle-outline text-warning mr-2"></i>
                  <strong>Note:</strong> While we implement industry-standard measures, no method of online transmission or electronic storage is completely secure, and we cannot guarantee absolute security.
                </div>
              </b-card-body>
            </b-card>

            <!-- Third-Party Links -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="third-party">
              <b-card-header class="bg-light">
                <h4 class="mb-0">6. Third-Party Links & Services</h4>
              </b-card-header>
              <b-card-body>
                <div class="d-flex align-items-start">
                  <!-- <img src="@/assets/images/third-party-logos.png" alt="Third Party Services" class="img-fluid rounded mr-4" style="max-width: 200px;"> -->
                  <div>
                    <p>Our Platform may link to external websites and services that have their own privacy policies. OVVURA is not responsible for the data practices or content of those third parties. If you provide personal information on their sites, different rules may apply. Please review their privacy policies and contact them directly with any questions.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Cookies -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="cookies">
              <b-card-header class="bg-light">
                <h4 class="mb-0">7. Cookies & Tracking Technologies</h4>
              </b-card-header>
              <b-card-body>
                <p>We use cookies, web beacons, and similar technologies to enhance your experience, analyze site usage, and deliver personalized content.</p>
                
                <div class="cookie-types">
                  <div class="cookie-type">
                    <div class="cookie-icon bg-danger text-white">
                      <i class="mdi mdi-shield-check"></i>
                    </div>
                    <h5>Essential Cookies</h5>
                    <p>Required for core functionality</p>
                  </div>
                  
                  <div class="cookie-type">
                    <div class="cookie-icon bg-info text-white">
                      <i class="mdi mdi-chart-line"></i>
                    </div>
                    <h5>Performance Cookies</h5>
                    <p>Help us understand how visitors use the Platform</p>
                  </div>
                  
                  <div class="cookie-type">
                    <div class="cookie-icon bg-success text-white">
                      <i class="mdi mdi-cog"></i>
                    </div>
                    <h5>Functional Cookies</h5>
                    <p>Remember preferences and settings</p>
                  </div>
                  
                  <div class="cookie-type">
                    <div class="cookie-icon bg-warning text-white">
                      <i class="mdi mdi-bullseye"></i>
                    </div>
                    <h5>Marketing Cookies</h5>
                    <p>Deliver relevant ads and measure campaign effectiveness</p>
                  </div>
                </div>
                
                <div class="mt-4">
                  <p>You can manage or disable cookies via your browser settings. Disabling cookies may impair certain features and site performance.</p>
                </div>
              </b-card-body>
            </b-card>

            <!-- Your Rights -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="your-rights">
              <b-card-header class="bg-light">
                <h4 class="mb-0">8. Your Rights & Choices</h4>
              </b-card-header>
              <b-card-body>
                <div class="rights-grid">
                  <div class="right-card">
                    <div class="right-icon bg-primary text-white">
                      <i class="mdi mdi-file-document"></i>
                    </div>
                    <h5>Access & Correction</h5>
                    <p>Request a copy of your personal data or correct inaccuracies</p>
                  </div>
                  
                  <div class="right-card">
                    <div class="right-icon bg-danger text-white">
                      <i class="mdi mdi-delete"></i>
                    </div>
                    <h5>Deletion</h5>
                    <p>Request removal of your data, subject to legal retention requirements</p>
                  </div>
                  
                  <div class="right-card">
                    <div class="right-icon bg-success text-white">
                      <i class="mdi mdi-email-remove"></i>
                    </div>
                    <h5>Opt-Out</h5>
                    <p>Unsubscribe from marketing emails at any time</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Policy Changes -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="policy-changes">
              <b-card-header class="bg-light">
                <h4 class="mb-0">9. Changes to Policies & Terms</h4>
              </b-card-header>
              <b-card-body>
                <div class="d-flex align-items-start">
                  <i class="mdi mdi-alert-circle text-warning mr-3 mt-1" style="font-size: 2rem;"></i>
                  <div>
                    <p>We may update this Privacy Policy, Cookie Policy, or Terms & Conditions to meet evolving legal, regulatory, or business requirements. Changes take effect on the date they are posted. We encourage you to review these sections frequently to stay informed.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Contact -->
            <b-card no-body class="border-0 shadow-sm">
              <b-card-body class="text-center bg-light">
                <h4 class="mb-4">10. Contact Us</h4>
                <div class="contact-methods">
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-email text-primary mr-2"></i>
                    <span>Email: <a href="mailto:support@ovvura.ae">support@ovvura.ae</a></span>
                  </div>
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-phone text-primary mr-2"></i>
                    <span>Phone: <a href="tel:+971525495829">+971 52 549 5829</a></span>
                  </div>
                  <div class="contact-method">
                    <i class="mdi mdi-map-marker text-primary mr-2"></i>
                    <span>Address: Le Pont 2, Jumeirah, Dubai, UAE</span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  methods: {
    scrollToDetails() {
      const element = document.getElementById('information-collected');
      element.scrollIntoView({ behavior: 'smooth' });
    },
    downloadPolicy() {
      // In a real implementation, this would trigger a PDF download
      this.$bvToast.toast('Privacy Policy PDF download would start here', {
        title: 'Download',
        variant: 'info',
        solid: true
      });
    }
  }
}
</script>

<style scoped>
.privacy-page {
  overflow-x: hidden;
}

.privacy-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 5rem 0;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.data-categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.data-category {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.category-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.usage-features {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.usage-feature {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.usage-feature i {
  font-size: 1.5rem;
  margin-top: 3px;
}

.sharing-partners {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.partner-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.partner-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.security-note {
  background-color: #f8f9fa;
  border-left: 4px solid #6e8efb;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.security-features {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.security-feature {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.security-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.cookie-types {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.cookie-type {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.cookie-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.rights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.right-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.right-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.contact-method {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .privacy-hero {
    padding: 3rem 0;
  }
  
  .data-categories-grid,
  .sharing-partners,
  .security-features,
  .cookie-types,
  .rights-grid {
    grid-template-columns: 1fr;
  }
  
  .d-flex.align-items-start {
    flex-direction: column;
  }
  
  .d-flex.align-items-start img {
    margin-right: 0;
    margin-bottom: 20px;
    max-width: 100% !important;
  }
}
</style>